import { useBalanceStore } from '@/context/UserBalance'
import { getTokenByDenom } from '@/services/token-service'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Icon,
  Input,
  Box,
  Button,
  Text,
  Skeleton,
  SimpleGrid,
} from '@chakra-ui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { BankDropdown } from '../CurrencyDropdown/CurrencyDropdown'
import { useLazyQuery } from '@apollo/client'
import { GET_USER_PROFILE, GET_USERS_BY_NAME } from '@/api/query'
import { getEthereumAddress, MsgExecuteContractCompat, TokenMoney } from '@ninjablaze/sdk-ts'
import { useDebounce } from '@/hooks/useDebounce'
import { useTranslation } from 'next-i18next/pages'
import { tryCatch } from '@/helpers/trycatch'
import { useOutsideAlerter } from '@/hooks/useOutsideAlerter'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { NoResultsFallback } from '../NoResultsFallback/NoResultsFallback'
import CloseIcon from '@/public/assets/icons/close.svg'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import SendTipIcon from '@/public/assets/icons/get-money.svg'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { BONUS_ACCOUNT_CONTRACT, EXPLORERS, USDC_NEW_DENOM } from '@/constants/constants'
import { useSession } from '@/context/SessionProvider'
import { BsPencilFill } from 'react-icons/bs'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'

export const SendTipModal = ({ onClose, receiver }: { onClose: () => void; receiver?: User }) => {
  const { t } = useTranslation()
  const [view, setView] = useState(View.Form)

  return (
    <Modal size={'sm'} closeOnEsc={false} closeOnOverlayClick={false} isCentered={true} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader textAlign={'center'} pb={2} fontSize="xl" fontWeight="700">
          {view === View.Form ? t('common.send-tip') : t('common.successfully-sent')}
        </ModalHeader>
        <ModalCloseButton top={'19px'} right={6} onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          <SendTip view={view} receiver={receiver} setView={(view: View) => setView(view)} onClose={onClose} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

enum View {
  Form,
  Confirmation,
}

type Summary = {
  avatar: string
  username: string
  token: {
    amount: string
    denom: string
  }
  tx: string
}

const SendTip = ({
  view,
  setView,
  onClose,
  receiver,
}: {
  view: View
  setView: (view: View) => void
  onClose: () => void
  receiver?: User
}) => {
  const [summary, setSummary] = useState<Summary | null>(null)

  if (view === View.Confirmation) {
    return (
      <Confirmation
        handleBack={() => {
          setSummary(null)
          setView(View.Form)
        }}
        onClose={onClose}
        {...summary!}
      />
    )
  }

  return <SendTipForm initialReceiver={receiver} setSummary={setSummary} setView={(view) => setView(view)} />
}

const Confirmation = ({
  handleBack,
  onClose,
  avatar,
  username,
  token,
  tx,
}: {
  handleBack: () => void
  onClose: () => void
  avatar: string
  username: string
  token: { amount: string; denom: string }
  tx: string
}) => {
  const { t } = useTranslation()

  return (
    <Box pt={3}>
      <Flex justifyContent={'center'} mb={3}>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
          boxShadow={'0px 3.643px 43.714px 0px #68F276 inset;'}
          rounded={'full'}
          boxSize={'102px'}
          pos={'relative'}
          mr={'-30px'}
        >
          <Icon as={SendTipIcon} color={'brand.green'} w={12} h={12} />
        </Flex>
        <Box boxSize={'102px'}>
          <AvatarCircle size={'102px'} src={avatar} />
        </Box>
      </Flex>
      <Flex mb={3} justifyContent={'center'}>
        <Text fontSize={'xl'} fontWeight={700} textAlign={'center'}>
          {t('tip.sent-to', { username })}
        </Text>
      </Flex>
      <Flex justifyContent={'center'} mb={4}>
        <FiatOrCryptoAmount
          iconSize={6}
          wrapperStyles={{
            fontSize: 'xl',
          }}
          value={TokenMoney.fromHuman(token.amount, token.denom)}
        />
      </Flex>
      <StatsRow
        name={t('common.transaction-id')}
        value={<CopiableLink value={tx} link={EXPLORERS.INJECTIVE_TRANSACTIONS + tx} />}
      />
      <SimpleGrid mt={5} w={'full'} gap={2} columns={2}>
        <Button rounded={'xl'} onClick={onClose}>
          {t('common.done')}
        </Button>
        <Button onClick={handleBack} rounded={'xl'} variant={'secondary'}>
          {t('tip.send-another')}
        </Button>
      </SimpleGrid>
    </Box>
  )
}

const SendTipForm = ({
  setView,
  setSummary,
  initialReceiver,
}: {
  setView: (view: View) => void
  setSummary: (summary: Summary | null) => void
  initialReceiver?: User
}) => {
  const { t } = useTranslation()
  const { sendTransaction, account } = useSession()
  const [receiver, setReceiver] = useState<User | null>(initialReceiver ? initialReceiver : null)
  const balance = useBalanceStore((state) => state.balance)
  const [value, setValue] = useState('')
  const [denom, setDenom] = useState<string>(USDC_NEW_DENOM)
  const modal = useAppModal()
  const [userError, setUserError] = useState<string | null>(null)
  const [amountError, setAmountError] = useState<string | null>(null)

  const method = getTokenByDenom(denom)
  const fiatStore = useFiatCurrencyStore()
  const isFiatMode = method?.currency === SupportedCurrencies.USDC && !fiatStore.isUsd()
  const [fiatDisplay, setFiatDisplay] = useState('')

  useEffect(() => {
    if (!isFiatMode) return
    const usdc = parseFloat(value)
    setFiatDisplay(!isNaN(usdc) && usdc > 0 ? String(parseFloat(fiatStore.usdcToFiat(usdc).toFixed(6))) : '')
  }, [isFiatMode])

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (!receiver || !value) {
        if (!receiver) {
          setUserError(t('tip.user-required'))
        }

        if (!value) {
          setAmountError(t('common.amount-required'))
        }

        return
      }

      const tx = await sendTransaction(
        MsgExecuteContractCompat.fromJSON({
          sender: account?.injectiveAddress!,
          contractAddress: BONUS_ACCOUNT_CONTRACT,
          funds: [TokenMoney.fromHuman(value, denom).toCoin()],
          msg: {
            send_tips: {
              player: receiver.address,
              // msg: '',
            },
          },
        })
      )

      setSummary({
        username: receiver.username,
        avatar: receiver.avatar,
        token: {
          amount: value,
          denom,
        },
        tx: tx?.txHash!,
      })
      setView(View.Confirmation)

      return null
    },
  })

  useEffect(() => {
    if (balance[denom].bank.humanBigNumber.isLessThan(value)) {
      setAmountError(t('common.insufficient-funds'))
      return
    }

    setAmountError(null)
  }, [value, denom])

  useEffect(() => {
    setUserError(null)
  }, [receiver])

  const isNumeric = (num: string) => /^\d*\.{0,1}\d*$/.test(num)

  const handleChangeValue = (value: string) => {
    if ((!isNaN(Number(value)) && value.length <= 8 && isNumeric(value)) || value === '') {
      setValue(value)
    }
  }

  return (
    <>
      <Flex mb={3} justifyContent={'space-between'} alignItems={'end'}>
        <Text fontSize={'sm'} fontWeight={700}>
          {t('tip.to-user')}
        </Text>
        {!!receiver && (
          <Text
            color={'ninja.200'}
            cursor={'pointer'}
            fontSize={'sm'}
            fontWeight={700}
            _hover={{
              color: 'ninja.100',
            }}
            onClick={() => modal.openModalNew(AppModals.UserStats, { address: receiver.address })}
          >
            {t('common.show-profile')}
          </Text>
        )}
      </Flex>
      <Box mb={4}>
        <ReceiverDropdown isError={!!userError} isDisabled={isPending} setReceiver={setReceiver} receiver={receiver} />
        {!!userError && (
          <Text mt={1} ml={1} lineHeight={1.2} fontSize={'xs'} color={'brand.error'} fontWeight={700}>
            {userError}
          </Text>
        )}
      </Box>
      <Flex mb={1.5} justifyContent={'space-between'} alignItems={'end'}>
        <Text fontWeight={700} fontSize={'sm'} mb={1.5}>
          {t('tip.amount-to-send')}
        </Text>
        <BankDropdown denom={denom} setDenom={setDenom} isDisabled={isPending} />
      </Flex>
      <Box mb={3}>
        <Flex
          mb={1.5}
          bg={'ninja.500'}
          rounded={'xl'}
          flexBasis={'full'}
          px={5}
          h={'56px'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={2}
          border={'2px solid'}
          borderColor={!!amountError ? 'brand.error' : 'ninja.500'}
        >
          {!isFiatMode && method.icon(6)}
          <Input
            value={isFiatMode ? fiatDisplay : value}
            isDisabled={isPending}
            onChange={(e) => {
              const raw = e.target.value.replace(',', '.')
              if (isFiatMode) {
                if (raw && !/^[\d]*\.?[\d]*$/.test(raw)) return
                setFiatDisplay(raw)
                const fiatNum = parseFloat(raw)
                setValue(!isNaN(fiatNum) && fiatNum > 0 ? fiatStore.fiatToUsdc(fiatNum).toFixed(6) : '')
                return
              }
              handleChangeValue(raw)
            }}
            placeholder={'100' + ' ' + (isFiatMode ? fiatStore.getCode() : method.name)}
            _placeholder={{
              color: 'ninja.200',
            }}
            fontWeight={700}
            variant={'unstyled'}
            flexBasis={'full'}
          />
          {isFiatMode && (
            <Text flexShrink={0} fontSize={'sm'} fontWeight={700} color={'ninja.300'}>
              {fiatStore.getSymbol()}
            </Text>
          )}
        </Flex>
        {!!amountError && (
          <Text mt={1} ml={1} lineHeight={1.2} fontSize={'xs'} color={'brand.error'} fontWeight={700}>
            {amountError}
          </Text>
        )}
      </Box>

      <Box mt={4}>
        <Button
          onClick={() => mutate()}
          isLoading={isPending}
          w={'full'}
          rounded={'xl'}
          h={'56px'}
          isDisabled={!!amountError || !!userError}
        >
          {t('common.send')}
        </Button>
      </Box>
    </>
  )
}

type User = {
  avatar: string
  username: string
  address: string
}

const ReceiverDropdown = ({
  setReceiver,
  receiver,
  isDisabled,
  isError,
}: {
  receiver: User | null
  setReceiver: (receiver: User | null) => void
  isDisabled: boolean
  isError: boolean
}) => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [isActive, setActive] = useState(false)
  const wrapperRef = useRef(null)
  const [autoFocus, setAutoFocus] = useState(false)

  useOutsideAlerter(wrapperRef, () => {
    if (isActive) {
      setReceiver(null)
    }
    setActive(false)
  })

  const handleChangeValue = (value: string) => {
    setValue(value)
  }

  return (
    <Flex
      bg={'ninja.500'}
      rounded={'xl'}
      flexBasis={'full'}
      h={'56px'}
      alignItems={'center'}
      justifyContent={'center'}
      gap={2}
      border={'2px solid'}
      pos={'relative'}
      borderColor={isError ? 'brand.error' : 'ninja.500'}
      ref={wrapperRef}
    >
      {!!receiver ? (
        <Flex
          alignItems={'center'}
          justifyContent={'space-between'}
          px={5}
          w={'full'}
          h={'full'}
          color={'ninja.200'}
          _hover={{
            color: isDisabled ? 'ninja.200' : 'ninja.100',
          }}
          cursor={isDisabled ? 'default' : 'pointer'}
          opacity={isDisabled ? 0.6 : 1}
          fontSize={'sm'}
          fontWeight={700}
          onClick={() => {
            if (!isDisabled) {
              setReceiver(null)
            }
          }}
        >
          <Flex color={'white'} gap={2} alignItems={'center'}>
            <AvatarCircle size={'sm'} src={receiver.avatar} />
            {receiver.username}
          </Flex>
          <Icon transition={'0.2s'} w={'auto'} h={'auto'} as={CloseIcon} />
        </Flex>
      ) : (
        <Input
          px={5}
          autoFocus={autoFocus}
          value={value}
          onFocus={() => {
            setActive(true)
          }}
          onChange={(e) => handleChangeValue(e.target.value)}
          placeholder={t('tip.enter-nickname-or-address')}
          _placeholder={{
            color: 'ninja.200',
          }}
          fontWeight={700}
          variant={'unstyled'}
          flexBasis={'full'}
        />
      )}
      {isActive && (
        <DropdownList
          value={value}
          setValue={(user: User) => setReceiver(user)}
          onClose={() => {
            setAutoFocus(true)
            setActive(false)
          }}
        />
      )}
    </Flex>
  )
}

const DropdownList = ({
  value,
  onClose,
  setValue,
}: {
  onClose: () => void
  setValue: (user: User) => void
  value: string
}) => {
  const { t } = useTranslation()
  const { value: queryValue } = useDebounce(value)
  const [isDebouncing, setDebouncing] = useState(false)

  const [loadUsers] = useLazyQuery(GET_USERS_BY_NAME, { variables: { name: queryValue } })
  const [loadUserByAddress] = useLazyQuery(GET_USER_PROFILE, { variables: { address: queryValue } })

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['USERS_PROFILES', queryValue],
    queryFn: async () => {
      setDebouncing(false)
      if (queryValue.length < 3) {
        return null
      }

      const { data } = tryCatch(() => getEthereumAddress(queryValue))

      if (!!data) {
        return loadUserByAddress({ variables: { address: queryValue } }).then((data) => [
          {
            avatar: data.data?.user?.profileAvatar as string,
            username: data.data?.user?.profileUserName as string,
            address: queryValue as string,
          },
        ])
      }

      const users = await loadUsers({ variables: { name: queryValue } }).then(
        (data) =>
          data.data?.users?.nodes.map((user) => ({
            avatar: user?.profileAvatar as string,
            username: user?.profileUserName as string,
            address: user?.id as string,
          })) || []
      )

      return users
    },
    retry: false,
    enabled: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (value.length >= 3) {
      setDebouncing(true)
    }
  }, [value])

  useEffect(() => {
    refetch()
  }, [queryValue])

  if (isFetching || isDebouncing) {
    return (
      <Box
        boxShadow={'2xl'}
        px={2}
        py={3}
        rounded={'xl'}
        w={'full'}
        zIndex={10}
        pos={'absolute'}
        top={'100%'}
        mt={2}
        bg={'ninja.500'}
      >
        {Array.from({ length: 5 }).map((_, k) => (
          <Skeleton key={k} mb={1} h={'44px'} rounded={'xl'} startColor="ninja.600" endColor="ninja.700" w={'full'} />
        ))}
      </Box>
    )
  }

  if (queryValue.length < 3) {
    return (
      <Box
        boxShadow={'2xl'}
        px={2}
        py={3}
        rounded={'xl'}
        w={'full'}
        zIndex={10}
        pos={'absolute'}
        top={'100%'}
        mt={2}
        bg={'ninja.500'}
      >
        <NoResultsFallback
          icon={<Icon as={BsPencilFill} w={6} h={6} />}
          styles={{
            h: '100px',
            rounded: 'xl',
            mb: 0,
          }}
          text={t('common.type-to-search')}
        />
      </Box>
    )
  }

  if (!data || data?.length === 0) {
    return (
      <Box
        boxShadow={'2xl'}
        px={2}
        py={3}
        rounded={'xl'}
        w={'full'}
        zIndex={10}
        pos={'absolute'}
        top={'100%'}
        mt={2}
        bg={'ninja.500'}
      >
        <NoResultsFallback
          styles={{
            h: '100px',
            rounded: 'xl',
            mb: 0,
          }}
          text={t('messages.no-players-found')}
        />
      </Box>
    )
  }

  return (
    <Box
      boxShadow={'2xl'}
      px={2}
      py={3}
      rounded={'xl'}
      w={'full'}
      zIndex={10}
      pos={'absolute'}
      top={'100%'}
      mt={2}
      bg={'ninja.500'}
      maxH={'300px'}
      overflowY={'auto'}
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#15143d',
          borderRadius: '24px',
        },
      }}
    >
      {data?.map((user) => (
        <Flex
          mb={1}
          py={1.5}
          px={2}
          rounded={'xl'}
          _hover={{
            bg: 'ninja.600',
          }}
          w={'full'}
          alignItems={'center'}
          gap={2}
          fontSize={'sm'}
          cursor={'pointer'}
          fontWeight={700}
          key={user.address}
          onClick={() => {
            setValue(user)
            onClose()
          }}
        >
          <AvatarCircle size={'sm'} src={user.avatar} />
          {user.username}
        </Flex>
      ))}
    </Box>
  )
}
